import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import api from '../../services/api';
import { logout } from '../../store/slices/authSlice';
import { sessionIdleTimeoutMin } from '../../utils/sessionPolicy';

const ACTIVITY_KEY = 'lastActivityAt';
const WARN_BEFORE_MS = 60_000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;

/** Signs admin users out after Session Timeout minutes of inactivity (shared across tabs). */
export default function IdleLogoutGuard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const lastWrite = useRef(0);

  const markActive = useCallback(() => {
    const now = Date.now();
    if (now - lastWrite.current < 5_000) return;
    lastWrite.current = now;
    localStorage.setItem(ACTIVITY_KEY, String(now));
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch {
      /* already expired */
    }
    dispatch(logout());
    navigate('/login?reason=SESSION_IDLE_TIMEOUT');
  }, [dispatch, navigate]);

  useEffect(() => {
    const timeoutMin = sessionIdleTimeoutMin();
    if (!timeoutMin) return undefined;
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const timer = window.setInterval(() => {
      const last = Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now();
      const remaining = timeoutMin * 60_000 - (Date.now() - last);
      if (remaining <= 0) {
        window.clearInterval(timer);
        void signOut();
      } else if (remaining <= WARN_BEFORE_MS) {
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        setSecondsLeft(null);
      }
    }, 1_000);
    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, [markActive, signOut]);

  return (
    <Dialog open={secondsLeft != null} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Still there?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          You will be signed out in <strong>{secondsLeft}s</strong> because of inactivity (Session Timeout set in Site Settings → Security).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void signOut()}>Sign out now</Button>
        <Button
          variant="contained"
          onClick={() => {
            lastWrite.current = 0;
            markActive();
            setSecondsLeft(null);
          }}
        >
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
